import { useMemo, useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
  Button,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useAuth } from "../../context/AuthContext";
import logo from "../../assets/pawsi.jpeg";

const BRAND = {
  olive: "#757B2B",
  oliveDark: "#5F641F",
  oliveLight: "#8E9440",
  cream: "#FBF7E8",
  warmBg: "#F7F5EF",
  ink: "#1E2218",
  ink2: "#5E6553",
  slate: "#64748B",        // Gris azulado profesional
  slateLight: "#94A3B8",
  surface: "#F8FAFC",
  border: "#E2E8F0",
};

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const successMessage = String(location.state?.message || "").trim();

  const canSubmit = useMemo(
    () => Boolean(username.trim()) && Boolean(password) && !submitting,
    [username, password, submitting]
  );

  if (isAuthenticated) return <Navigate to="/" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setSubmitting(true);
    setError("");
    try {
      await login({ username: username.trim(), password });
      navigate("/", { replace: true });
    } catch {
      setError("Credenciales inválidas. Verifica usuario y contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: `
          radial-gradient(1100px 680px at 15% 12%, ${BRAND.olive}0D, transparent 62%),
          radial-gradient(950px 580px at 82% 18%, ${BRAND.slate}08, transparent 68%),
          radial-gradient(820px 520px at 50% 85%, ${BRAND.olive}06, transparent 74%),
          linear-gradient(180deg, ${BRAND.surface} 0%, ${BRAND.warmBg} 100%)
        `,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          borderRadius: 3,
          border: `1px solid ${BRAND.border}`,
          background: "rgba(255,255,255,0.98)",
          backdropFilter: "blur(12px)",
          boxShadow: `
            0 20px 60px rgba(30,34,24,0.06), 
            0 4px 16px rgba(30,34,24,0.03)
          `,
          overflow: "hidden",
        }}
      >
        {/* Línea de acento superior - solo olive */}
        <Box
          sx={{
            height: 3,
            background: `linear-gradient(90deg, ${BRAND.oliveDark}, ${BRAND.oliveLight})`,
          }}
        />

        <Box sx={{ px: 5, pt: 5, pb: 4 }}>
          {/* Header con logo */}
          {/* <Stack direction="row" spacing={2.5} alignItems="center" mb={4}>
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: `${BRAND.cream}`,
                border: `1px solid ${BRAND.olive}20`,
              }}
            >
              <Box
                component="img"
                src={logo}
                alt="Pawsi Patitas"
                sx={{
                  width: 42,
                  height: 42,
                  objectFit: "contain",
                }}
              />
            </Box>
            <Box>
              <Typography 
                sx={{ 
                  fontWeight: 700, 
                  fontSize: '1.4rem',
                  lineHeight: 1.2, 
                  color: BRAND.ink,
                  letterSpacing: '-0.02em'
                }}
              >
                Iniciar sesión
              </Typography>
              <Typography 
                sx={{ 
                  fontSize: 13.5, 
                  color: BRAND.slate, 
                  mt: 0.3,
                  fontWeight: 400
                }}
              >
                Panel de gestión · Pawsi Patitas
              </Typography>
            </Box>
          </Stack> */}
          {/* Header con logo - más grande y dominante */}
 {/* Header con logo - compacto y bien proporcionado */}
<Stack direction="row" spacing={3} alignItems="center" mb={4}>
  <Box
    sx={{
      width: 72,
      height: 72,
      borderRadius: 2.5,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#FFFFFF',
      border: `2px solid ${BRAND.olive}18`,
      boxShadow: `0 4px 20px ${BRAND.olive}10, 0 1px 4px rgba(0,0,0,0.04)`,
      padding: 1.5,
      flexShrink: 0,
    }}
  >
    <Box
      component="img"
      src={logo}
      alt="Pawsi Patitas"
      sx={{
        width: '100%',
        height: '100%',
        objectFit: "contain",
      }}
    />
  </Box>
  
  <Box>
    <Typography 
      sx={{ 
        fontWeight: 700, 
        fontSize: '1.5rem',
        lineHeight: 1.2, 
        color: BRAND.ink,
        letterSpacing: '-0.02em'
      }}
    >
      Pawsi Patitas
    </Typography>
    <Typography 
      sx={{ 
        fontSize: 14, 
        color: BRAND.slate, 
        fontWeight: 400,
        mt: 0.3
      }}
    >
      Iniciar sesión · Punto de venta
    </Typography>
  </Box>
</Stack>
          <Stack spacing={2.5} component="form" onSubmit={onSubmit}>
            {error ? (
              <Alert
                severity="error"
                sx={{
                  borderRadius: 2,
                  border: `1px solid #FECACA`,
                  backgroundColor: '#FEF2F2',
                  color: '#991B1B',
                  '& .MuiAlert-icon': { color: '#DC2626' }
                }}
              >
                {error}
              </Alert>
            ) : null}

            {!error && successMessage ? (
              <Alert
                severity="success"
                sx={{
                  borderRadius: 2,
                  border: `1px solid ${BRAND.olive}30`,
                  backgroundColor: `${BRAND.olive}08`,
                  color: BRAND.oliveDark,
                  '& .MuiAlert-icon': { color: BRAND.olive }
                }}
              >
                {successMessage}
              </Alert>
            ) : null}

            <TextField
              label="Usuario"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              disabled={submitting}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: BRAND.surface,
                  '& fieldset': {
                    borderColor: BRAND.border,
                    transition: 'all 0.2s ease'
                  },
                  '&:hover fieldset': {
                    borderColor: BRAND.slateLight
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: BRAND.olive,
                    borderWidth: '2px'
                  },
                },
                '& .MuiInputLabel-root': {
                  color: BRAND.slate,
                  '&.Mui-focused': {
                    color: BRAND.oliveDark,
                    fontWeight: 500
                  },
                },
              }}
            />

            <TextField
              label="Contraseña"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              disabled={submitting}
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: BRAND.surface,
                  '& fieldset': {
                    borderColor: BRAND.border,
                    transition: 'all 0.2s ease'
                  },
                  '&:hover fieldset': {
                    borderColor: BRAND.slateLight
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: BRAND.olive,
                    borderWidth: '2px'
                  },
                },
                '& .MuiInputLabel-root': {
                  color: BRAND.slate,
                  '&.Mui-focused': {
                    color: BRAND.oliveDark,
                    fontWeight: 500
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((s) => !s)}
                      edge="end"
                      disabled={submitting}
                      aria-label="Mostrar u ocultar contraseña"
                      sx={{ color: BRAND.slate }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              disabled={!canSubmit}
              fullWidth
              sx={{
                mt: 1,
                borderRadius: 2,
                py: 1.4,
                fontWeight: 600,
                fontSize: 15,
                textTransform: 'none',
                letterSpacing: '0.01em',
                backgroundColor: BRAND.olive,
                color: '#FFFFFF',
                border: 'none',
                boxShadow: `0 4px 14px ${BRAND.olive}30`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: BRAND.oliveDark,
                  boxShadow: `0 6px 20px ${BRAND.olive}40`,
                  transform: 'translateY(-1px)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                  boxShadow: `0 2px 8px ${BRAND.olive}20`
                },
                '&.Mui-disabled': {
                  backgroundColor: `${BRAND.slate}15`,
                  color: `${BRAND.slate}50`,
                  boxShadow: 'none',
                },
              }}
            >
              {submitting ? (
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <CircularProgress size={18} sx={{ color: '#FFFFFF' }} />
                  <span>Verificando credenciales…</span>
                </Stack>
              ) : (
                "Ingresar"
              )}
            </Button>

            <Divider sx={{ my: 1 }}>
              <Typography sx={{ fontSize: 11, color: BRAND.slate, px: 1, fontWeight: 400 }}>
                ¿Necesitas ayuda?
              </Typography>
            </Divider>

            <Stack direction="row" justifyContent="center">
              <Button
                type="button"
                onClick={() => navigate("/forgot-password")}
                disabled={submitting}
                sx={{
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: 13.5,
                  color: BRAND.oliveDark,
                  py: 0.8,
                  px: 2,
                  '&:hover': {
                    backgroundColor: `${BRAND.olive}0D`,
                    color: BRAND.oliveDark
                  },
                }}
              >
                ¿Olvidaste tu contraseña?
              </Button>
            </Stack>

            <Typography
              sx={{
                fontSize: 11.5,
                color: BRAND.slate,
                textAlign: 'center',
                mt: 1,
                fontWeight: 400,
                opacity: 0.8
              }}
            >
              © {new Date().getFullYear()} Pawsi Patitas · Todos los derechos reservados
            </Typography>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
}

export default Login;